// @ts-check
/**
 * @typedef {'shared' | 'aws' | 'ms'} FieldType
 * @typedef {'contact' | 'report' | 'list' | 'all'} FieldContext
 * @typedef {'basic' | 'role' | 'business' | 'location' | 'relationship' | 'classification' | 'strength' | 'source'} FieldCategory
 *
 * @typedef {Object} DisplayField
 * @property {string} label - Human-readable label
 * @property {string} field - Internal key
 * @property {FieldType} type - Data source
 * @property {FieldContext[]} contexts - Where this field is used
 * @property {FieldCategory} category - Field category for grouping
 * @property {boolean} [isDefault] - Whether this field is shown by default
 */

/** @type {DisplayField[]} */
export const displayFields = [
    // Basic Information
    {
        label: "Contact ID",
        field: "contact_id",
        type: "shared",
        contexts: ["all"],
        category: "basic",
    },
    {
        label: "Contact Name",
        field: "contact_name",
        type: "shared",
        contexts: ["all"],
        category: "basic",
        isDefault: true
    },
    {
        label: "Email",
        field: "contact_email",
        type: "shared",
        contexts: ["all"],
        category: "basic",
        isDefault: true
    },
    {
        label: "Account Name",
        field: "account_name",
        type: "shared",
        contexts: ["all"],
        category: "basic",
        isDefault: true
    },
    {
        label: "Title",
        field: "title",
        type: "shared",
        contexts: ["all"],
        category: "role",
        isDefault: true
    },

    // Classification Information
    {
        label: "Contact Group",
        field: "contact_group",
        type: "shared",
        contexts: ["all"],
        category: "classification"
    },
    {
        label: "Contact Classification",
        field: "contact_classification",
        type: "shared",
        contexts: ["all"],
        category: "classification"
    },
    {
        label: "Role Category",
        field: "role_category",
        type: "shared",
        contexts: ["all"],
        category: "role"
    },
    {
        label: "Seniority Level",
        field: "seniority_level",
        type: "shared",
        contexts: ["all"],
        category: "role"
    },

    // Business Information
    {
        label: "Managed Accounts",
        field: "num_of_managed_accounts",
        type: "shared",
        contexts: ["all"],
        category: "business"
    },
    {
        label: "Account Type",
        field: "accounts_type_name",
        type: "shared",
        contexts: ["all"],
        category: "business"
    },
    {
        label: "Segment",
        field: "segment",
        type: "shared",
        contexts: ["all"],
        category: "business"
    },
    {
        label: "Specialty Area",
        field: "specialty_area",
        type: "shared",
        contexts: ["all"],
        category: "business"
    },

    // Strength Information
    {
        label: "Personal Strength",
        field: "personal_strength",
        type: "shared",
        contexts: ["all"],
        category: "strength"
    },
    {
        label: "Co-sell Willingness",
        field: "co-sell_willingness",
        type: "shared",
        contexts: ["all"],
        category: "strength"
    },

    // Relationship Information
    {
        label: "Reports To",
        field: "reports_to",
        type: "shared",
        contexts: ["all"],
        category: "relationship"
    },
    {
        label: "Number of Reportees",
        field: "num_of_reportees",
        type: "shared",
        contexts: ["all"],
        category: "relationship"
    },
    {
        label: "Time in Company",
        field: "time_in_company",
        type: "shared",
        contexts: ["all"],
        category: "relationship"
    },
    {
        label: "Time in Current Role",
        field: "time_in_current_role",
        type: "shared",
        contexts: ["all"],
        category: "relationship"
    },
    {
        label: "Previous Roles",
        field: "previous_roles",
        type: "shared",
        contexts: ["all"],
        category: "relationship"
    },

    // Location Information
    {
        label: "Region",
        field: "region",
        type: "shared",
        contexts: ["all"],
        category: "location"
    },
    {
        label: "Subsidiary",
        field: "subsidiary",
        type: "shared",
        contexts: ["all"],
        category: "location"
    },
    {
        label: "City",
        field: "city",
        type: "shared",
        contexts: ["all"],
        category: "location"
    },
    {
        label: "US District",
        field: "us_district",
        type: "shared",
        contexts: ["all"],
        category: "location"
    },

    // Additional Information
    {
        label: "Area of Interest",
        field: "area_of_interest",
        type: "shared",
        contexts: ["all"],
        category: "business"
    },
    {
        label: "Industry of Focus",
        field: "industry_of_focus",
        type: "shared",
        contexts: ["all"],
        category: "business"
    },
    {
        label: "Tags",
        field: "tags",
        type: "shared",
        contexts: ["all"],
        category: "classification"
    },
    {
        label: "Description",
        field: "description",
        type: "shared",
        contexts: ["all"],
        category: "basic"
    },

    // Source Information
    {
        label: "Initial Source",
        field: "initial_source",
        type: "shared",
        contexts: ["all"],
        category: "source"
    },
    {
        label: "Latest Source",
        field: "latest_source",
        type: "shared",
        contexts: ["all"],
        category: "source"
    },
    {
        label: "Last Updated",
        field: "timestamp",
        type: "shared",
        contexts: ["all"],
        category: "source"
    }
]; 
export const getDefaultFields = () => displayFields.filter(f => f.isDefault);
/**
 * @param {string[] | null} keys
 */
export const getDisplayFields = (isDefault=false,keys = null) => {
  const visibleFields = displayFields.filter(f => isDefault ? f.isDefault : true);

  if (!keys||keys.length==0) return visibleFields;

  return keys
    .map(key => visibleFields.find(f => f.field === key))
    .filter(Boolean);
};
